// P38-fix — PathSplash.
//
// A palette-neutral cream splash used during the pre-hydration window so
// the swap to PathHome (cream/terracotta palette) is visually invisible.
//
// Replaces the old Loading.jsx render — a giant yellow pencil emoji on a
// purple→cream gradient — that the user perceived as "old pencil /
// AdventureMap UI" flashing before PathHome snapped in. Recon showed the
// Loading screen occupied ~380ms of the ~800ms cold-load window on
// localhost (longer on slow networks).
//
// This component is used by:
//   - src/App/Loading.jsx (the gate-ladder + Suspense fallback)
//   - src/screens/PathHome.tsx (while useTodaysMission's React Query is
//     in-flight — without this, PathHome would render the path with a
//     wrong today-glow stone (idxRec=0 default) until mission resolves).
//
// data-testid="path-splash" is a stable hook for e2e assertions that the
// first paint on /play is the splash, NOT the purple-pencil Loading.
//
// H-A1 (Track H Wave A): the splash now paints the DESIGN-TOKEN cream
// (#FAF3E8, var(--cream)) instead of the legacy COLORS.bg (#FFFDF8) —
// post-H-0 the page background IS the token cream, so the old value
// produced a faint visible flash at swap time. Dots are token
// terracotta. Inline literals (not var()) so the splash renders
// correctly even before the stylesheet finishes loading — that is its
// whole job. Keep in sync with --cream / --terracotta in globals.css.

const CREAM = "var(--cream)"; // canonical --cream token (Phase 0 tokenize)
const TERRACOTTA = "var(--terracotta)"; // canonical --terracotta token (Phase 0 tokenize)

export function PathSplash({
  testId = "path-splash",
}: { testId?: string } = {}) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        // Cream matches PathHome's paper background (the --cream token)
        // so the swap is invisible to the eye. No gradient — solid colour
        // reads "in-progress, same surface" rather than "different screen
        // about to be replaced."
        background: CREAM,
      }}
    >
      <style>{`
        @keyframes pathSplashPulse {
          0%, 100% { opacity: 0.25; transform: scale(0.85); }
          50%      { opacity: 1;    transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-splash-dot] { animation: none !important; opacity: 0.6; }
        }
      `}</style>
      {/* Three-dot pulse, terracotta (PathHome's primary accent), staggered
          150ms apart. Reads as "loading" without dominating the screen the
          way the 80px pencil emoji did. */}
      <div
        aria-label="Loading"
        role="status"
        style={{ display: "flex", gap: 8 }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            data-splash-dot=""
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: TERRACOTTA,
              animation: "pathSplashPulse 1.2s ease-in-out infinite",
              animationDelay: `${i * 0.15}s`,
              display: "inline-block",
            }}
          />
        ))}
      </div>
    </div>
  );
}
